import { Directive, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';

// Every lightbox picture zooms in where it is clicked and follows the pointer from there; a second
// click, or paging to the next picture, zooms back out (owner 2026-11-04). The selector reaches all
// the lightboxes (global src/styles/page/tool-lightbox.scss), so no template has to ask for it.
@Directive( {
  selector: 'figure.cp-lightbox-inner',
} )
export class LightboxZoomDirective implements OnInit, OnDestroy {

  // The picture's place on screen before it was scaled, or null while it is not zoomed.
  private box: DOMRect | null = null;

  private readonly zoomOut = () => {
    this.box = null;
    this.host.nativeElement.classList.remove( 'is-zoomed' );
  };

  constructor( private host: ElementRef<HTMLElement> ) {
  }

  ngOnInit(): void {
    // load does not bubble, so it is caught on the way down; a new src means a new picture
    this.host.nativeElement.addEventListener( 'load', this.zoomOut, true );
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener( 'load', this.zoomOut, true );
  }

  @HostListener( 'click', [ '$event' ] )
  toggle( event: MouseEvent ): void {
    const picture = event.target;
    if ( !( picture instanceof HTMLImageElement ) ) {
      return;
    }
    event.stopPropagation();

    if ( this.box ) {
      this.zoomOut();
    } else if ( !picture.classList.contains( 'img-fallback' ) ) {
      this.box = picture.getBoundingClientRect();
      // the file's own pixels when it has them, never less than twice the size on screen
      const scale = Math.min( 4, Math.max( 2, picture.naturalWidth / this.box.width ) );
      this.host.nativeElement.style.setProperty( '--lb-zoom', String( scale ) );
      this.follow( event );
      this.host.nativeElement.classList.add( 'is-zoomed' );
    }
  }

  @HostListener( 'pointermove', [ '$event' ] )
  follow( event: MouseEvent ): void {
    if ( this.box ) {
      const style = this.host.nativeElement.style;
      style.setProperty( '--lb-x', this.percent( event.clientX - this.box.left, this.box.width ) );
      style.setProperty( '--lb-y', this.percent( event.clientY - this.box.top, this.box.height ) );
    }
  }

  private percent( offset: number, size: number ): string {
    return `${ Math.min( 100, Math.max( 0, offset / size * 100 ) ) }%`;
  }
}
